import { lang } from '../i18n'
import type { Reminder } from '../core/reminders'
import type { Phase, WakeState } from '../core/types'

/**
 * Клиент сервера-планировщика. Сервер нужен ради одного: прислать уведомление о смене фазы,
 * когда вкладка закрыта. Без него приложение работает, но будит только при включённом экране.
 *
 * Расписание отправляется целиком при каждом действии — так же, как Android переставляет
 * все будильники разом. Вместе с ним уходит время клиента: сервер считает расхождение часов
 * и хранит всё в своих, а обратно отдаёт уже в часах клиента.
 *
 * Слияние идёт по `updatedAt`: если на сервере запись свежее (например, нажали кнопку под
 * сообщением бота), он возвращает `applied: false` и своё состояние — его и надо принять.
 */

export interface ScheduleRequest {
  /** Чьё окно: расписание на сервере заведено на пару «устройство + малыш». */
  childKey: string
  programId: string
  phases: Phase[]
  /** null = окно не идёт */
  windowStart: number | null
  /** когда окно изменилось на клиенте, в его часах */
  updatedAt: number
  clientNow: number
  tz: string
  /** Язык получателя: по нему сервер соберёт тексты пушей (решение заказчика 14). */
  lang: string
  /** только сайт: подписка Web Push, уходит вместе с ближайшим расписанием */
  pushSubscription?: PushSubscriptionJSON
  /** Напоминания с готовым текстом: «пора вставать», ориентир по весу. Заменяют прежние. */
  reminders?: Reminder[]
}

/** Состояние с сервера, времена уже переведены в часы клиента. */
export interface ServerState {
  /** Чьё окно. Пусто — устройство без разделения на малышей (Android, часы). */
  childKey?: string
  programId: string
  phases: Phase[]
  windowStart: number | null
  updatedAt: number
  updatedBy: string
  serverNow: number
  clientNow: number
  /** есть ли у сервера способ доставить уведомление: подписка, бот или никак */
  delivery: 'push' | 'bot' | 'none'
}

export interface ScheduleResult {
  applied: boolean
  state: ServerState | null
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const TOKEN_KEY = 'moonwake.device_token'

/**
 * Базовый адрес API зависит от региона устройства: казахстанский сервер — тот же домен,
 * что и сайт; российский — отдельный хост (см. `api/region.ts`). Поэтому адрес берётся
 * функцией в момент запроса, а не константой при загрузке модуля.
 */
export { apiBase } from './region'
import { apiBase as база } from './region'

/** Открытый ключ Web Push. Сервер присылает его вместе с устройством и состоянием. */
let vapidKey: string | null = null
export function knownVapidKey(): string | null {
  return vapidKey
}

export function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function storedToken(): string | null {
  try {
    return globalThis.localStorage?.getItem(TOKEN_KEY) ?? null
  } catch {
    return null
  }
}

function rememberToken(token: string): void {
  try {
    globalThis.localStorage?.setItem(TOKEN_KEY, token)
  } catch {
    // приватный режим — обойдёмся без запоминания
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = storedToken()
  const response = await fetch(`${база()}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token === null ? {} : { authorization: `Device ${token}` }),
      ...init.headers,
    },
  })
  if (!response.ok) throw new ApiError(`${init.method ?? 'GET'} ${path} → ${response.status}`, response.status)
  return (await response.json()) as T
}

/**
 * Сервер забывает подписку этого устройства.
 *
 * Без этого он продолжит слать уведомления, пока служба доставки не ответит «подписки
 * нет», — а человек уже нажал «выключить» и ждёт тишины.
 */
export async function forgetPush(): Promise<void> {
  try {
    await request('/push', { method: 'DELETE' })
  } catch {
    // Сети нет — подписка всё равно отозвана в браузере, сервер упрётся в «нет адресата»
  }
}

/** Заводит устройство на сервере и запоминает токен; повторный вызов возвращает сохранённый. */
export async function ensureDevice(): Promise<string> {
  const existing = storedToken()
  if (existing !== null) return existing
  const { token, vapidPublicKey } = await request<{ token: string; vapidPublicKey: string | null }>('/device', {
    method: 'POST',
    body: JSON.stringify({ tz: timeZone(), lang: lang.value }),
  })
  vapidKey = typeof vapidPublicKey === 'string' ? vapidPublicKey : null
  rememberToken(token)
  return token
}

/** Состояние таймера в том виде, в каком его ждёт сервер. */
export function scheduleFrom(
  state: WakeState,
  updatedAt: number,
  now = Date.now(),
  pushSubscription?: PushSubscriptionJSON,
  childKey = '',
  reminders: Reminder[] = [],
): ScheduleRequest {
  return {
    childKey,
    programId: state.program.id,
    phases: state.program.phases,
    windowStart: state.windowStartMillis,
    updatedAt,
    clientNow: now,
    tz: timeZone(),
    // Язык получателя: по нему сервер соберёт тексты пушей (решение заказчика 14)
    lang: lang.value,
    ...(pushSubscription === undefined ? {} : { pushSubscription }),
    ...(reminders.length === 0 ? {} : { reminders }),
  }
}

export async function putSchedule(
  state: WakeState,
  updatedAt: number,
  now = Date.now(),
  pushSubscription?: PushSubscriptionJSON,
  childKey = '',
  reminders: Reminder[] = [],
): Promise<ScheduleResult> {
  const result = await request<ScheduleResult>('/schedule', {
    method: 'PUT',
    body: JSON.stringify(scheduleFrom(state, updatedAt, now, pushSubscription, childKey, reminders)),
  })
  return result
}

/** Состояние с сервера. `null` — расписание ещё ни разу не отправляли. */
export async function fetchState(now = Date.now(), childKey = ''): Promise<ServerState | null> {
  const { state, vapidPublicKey } = await request<{ state: ServerState | null; vapidPublicKey: string | null }>(
    `/state?clientNow=${now}${childKey === '' ? '' : `&child=${encodeURIComponent(childKey)}`}`,
  )
  if (typeof vapidPublicKey === 'string') vapidKey = vapidPublicKey
  return state
}

/**
 * Подписка на уведомления: ключ сервера, разрешение браузера, подписка.
 * Сама подписка на сервер не уходит: она уедет вместе с ближайшим расписанием.
 */
/**
 * Почему подписка не получилась. Молчаливый отказ здесь уже дважды стоил неработающих
 * уведомлений: человек нажимал кнопку, и ничего не происходило без единого следа.
 */
export type SubscribeFailure = 'unsupported' | 'denied' | 'no_server_key' | 'browser_refused'

export async function subscribeToPush(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscriptionJSON | SubscribeFailure> {
  if (!('PushManager' in globalThis)) return 'unsupported'
  // Разрешение уже спросил вызывающий, сразу после нажатия: браузер даёт на это
  // короткое окно, и сетевой запрос его съедает. Перепроверять здесь нельзя —
  // браузер записывает ответ не мгновенно, и подписка терялась в каждом втором случае.
  if (Notification.permission === 'denied') return 'denied'

  if (vapidKey === null) {
    await ensureDevice()
    if (vapidKey === null) await fetchState()
  }
  // Проверка именно на строку: сервер без настроенных ключей вернёт null, а старый
  // или урезанный ответ — вовсе ничего, и тогда преобразование ключа падало молча.
  if (typeof vapidKey !== 'string' || vapidKey === '') return 'no_server_key'

  try {
    const existing = await registration.pushManager.getSubscription()
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      }))
    return subscription.toJSON()
  } catch (error) {
    // Служба доставки браузера бывает недоступна: это не наша ошибка, но человеку
    // надо сказать правду, а не делать вид, что уведомления включены.
    console.warn('Браузер не дал подписку на уведомления:', error)
    return 'browser_refused'
  }
}

/** Пока страница видима, слушаем изменения с сервера: их приносят кнопки в сообщениях бота. */
export function listenToState(onState: (state: ServerState) => void): () => void {
  if (typeof EventSource === 'undefined') return () => {}
  const token = storedToken()
  const source = new EventSource(`${база()}/events${token === null ? '' : `?token=${encodeURIComponent(token)}`}`)
  source.addEventListener('state', (event) => {
    try {
      onState(JSON.parse((event as MessageEvent).data) as ServerState)
    } catch {
      // сервер прислал что-то неожиданное — переживём
    }
  })
  return () => source.close()
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}
